import { useState, useEffect } from 'react';
import { X, Download, Smartphone, Plus } from '../../icons/wp-icons';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { getHostBranding } from '../../utils/hostBranding';

// Animiertes iOS Share Icon
function AnimatedShareIcon({ className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-full h-full"
      >
        {/* Box */}
        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
        {/* Arrow */}
        <polyline points="16 6 12 2 8 6" className="animate-bounce-subtle" />
        <line x1="12" y1="2" x2="12" y2="15" className="animate-bounce-subtle" />
      </svg>
    </div>
  );
}

// Animiertes Plus Icon in Box (Zum Home-Bildschirm)
function AnimatedAddToHomeIcon({ className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-full h-full"
      >
        {/* Rounded rectangle */}
        <rect x="3" y="3" width="18" height="18" rx="3" />
        {/* Plus sign with pulse animation */}
        <line x1="12" y1="8" x2="12" y2="16" className="animate-pulse-subtle" />
        <line x1="8" y1="12" x2="16" y2="12" className="animate-pulse-subtle" />
      </svg>
    </div>
  );
}

// Pfeil der nach unten zeigt (Safari Toolbar)
function AnimatedArrowDown({ className = '' }) {
  return (
    <div className={`${className} animate-bounce`}>
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        className="w-full h-full"
      >
        <path d="M12 16l-6-6h12l-6 6z" />
      </svg>
    </div>
  );
}

export default function MobileInstallBanner({ forceShow = false, onClose }) {
  const [showBanner, setShowBanner] = useState(false);
  const { isIOS, isAndroid, isStandalone, triggerInstall } = usePWAInstall();
  const hostBranding = getHostBranding();
  const accentColor = hostBranding?.accent || '#378ab2';

  useEffect(() => {
    if (forceShow) {
      setShowBanner(true);
      return;
    }

    // Check if already dismissed recently
    const dismissed = localStorage.getItem('pwa-mobile-install-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismiss = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Show banner if:
    // - Mobile device (iOS or Android)
    // - Not installed
    // - Not dismissed in last 7 days
    const shouldShow = (isIOS || isAndroid) && !isStandalone && daysSinceDismiss > 7;

    if (shouldShow) {
      // Show after 2 seconds to not overwhelm user
      const timer = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [isIOS, isAndroid, isStandalone, forceShow]);

  const handleInstall = () => {
    if (isAndroid) {
      triggerInstall();
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    if (!forceShow) {
      localStorage.setItem('pwa-mobile-install-dismissed', Date.now().toString());
    }
    if (onClose) onClose();
  };

  const handleNeverShow = () => {
    setShowBanner(false);
    // Set far future date (100 years)
    localStorage.setItem('pwa-mobile-install-dismissed', (Date.now() + 100 * 365 * 24 * 60 * 60 * 1000).toString());
    if (onClose) onClose();
  };

  // Don't show if already installed or not mobile (unless forceShow)
  if (!showBanner || isStandalone || (!isIOS && !isAndroid && !forceShow)) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] animate-fade-in"
        onClick={handleDismiss}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 animate-slide-up">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 text-white relative" style={{ backgroundColor: accentColor }}>
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition"
              aria-label="Schließen"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-2">
              <img
                src={hostBranding?.logo || "/icons/icon-96x96.png"}
                alt={hostBranding?.name || "WerkPilot"}
                className="w-12 h-12 rounded-xl"
              />
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  App installieren
                </h2>
                <p className="text-sm text-white/90">
                  Für den besten Mobile-Zugriff
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="space-y-4">
              {/* Benefits */}
              <div className="rounded-xl p-4" style={{ backgroundColor: `${accentColor}0D` }}>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <Download className="w-5 h-5" style={{ color: accentColor }} />
                  Vorteile der App:
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span style={{ color: accentColor }} className="mt-0.5">&#10003;</span>
                    <span><strong>Offline-Modus:</strong> Arbeiten ohne Internet beim Kunden</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: accentColor }} className="mt-0.5">&#10003;</span>
                    <span><strong>Schneller Zugriff:</strong> Icon auf dem Startbildschirm</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: accentColor }} className="mt-0.5">&#10003;</span>
                    <span><strong>Native Erfahrung:</strong> Vollbild ohne Browser-Leiste</span>
                  </li>
                </ul>
              </div>

              {/* iOS Instructions with animated icons */}
              {isIOS && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    <AnimatedShareIcon className="w-5 h-5 text-blue-600" />
                    Installation auf iPhone/iPad:
                  </h3>

                  {/* Step 1 */}
                  <div className="flex gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center">
                      <AnimatedShareIcon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded">
                          Schritt 1
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">
                        Tippe auf das <strong>Teilen-Symbol</strong> in der Safari-Leiste unten
                      </p>
                      <div className="mt-2 flex justify-center">
                        <AnimatedArrowDown className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center">
                      <AnimatedAddToHomeIcon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded">
                          Schritt 2
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">
                        Scrolle und wähle <strong>&quot;Zum Home-Bildschirm&quot;</strong>
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center">
                      <Plus className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded">
                          Schritt 3
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">
                        Tippe auf <strong>&quot;Hinzufügen&quot;</strong> oben rechts
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-yellow-800">
                      <strong>Wichtig:</strong> Funktioniert nur in Safari, nicht in Chrome!
                    </p>
                  </div>
                </div>
              )}

              {/* Android Instructions */}
              {isAndroid && (
                <div className="rounded-xl p-4 border" style={{ backgroundColor: `${accentColor}1A`, borderColor: `${accentColor}4D` }}>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    <Download className="w-5 h-5" style={{ color: accentColor }} />
                    Installation auf Android:
                  </h3>
                  <p className="text-sm text-gray-700 mb-4">
                    Klicke auf den Button und folge den Anweisungen.
                  </p>
                  <button
                    onClick={handleInstall}
                    className="w-full text-white py-3 px-4 rounded-xl font-semibold transition flex items-center justify-center gap-2 active:scale-95"
                    style={{ backgroundColor: accentColor }}
                  >
                    <Download className="w-5 h-5" />
                    Jetzt installieren
                  </button>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-col gap-2">
              {isIOS && (
                <button
                  onClick={handleDismiss}
                  className="w-full text-white py-3 px-4 rounded-xl font-semibold transition active:scale-95"
                  style={{ backgroundColor: accentColor }}
                >
                  Verstanden
                </button>
              )}

              <button
                onClick={handleDismiss}
                className="w-full bg-gray-100 text-gray-700 py-2.5 px-4 rounded-xl font-medium hover:bg-gray-200 transition text-sm"
              >
                Später erinnern
              </button>

              {!forceShow && (
                <button
                  onClick={handleNeverShow}
                  className="w-full text-gray-500 py-2 text-sm hover:text-gray-700 transition"
                >
                  Nicht mehr anzeigen
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
